import React from 'react';
import ReactTable from 'react-table';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import Money from '../../common/money';
import { t } from '../../../modules/infra/i18n';
import {
  getFirstPaydownDate,
  getFormattedRepaidPercent,
  getLoanStatus,
  getLoanTitle,
  getRepaymentRate,
  getTotalLoanAmount,
} from '../utils';

const LoansTable = ({ loans, onLoanLinkClicked, currencySymbol, timezone }) => (
  <div data-automation-id="loans-list-table">
    <ReactTable
      data={loans}
      showPagination={false}
      minRows={0}
      resizable={false}
      className="c-table-new c-table-with-sticky-header c-loans-table u-mt"
      columns={[
        {
          Header: t('loans:table.header_offer'),
          id: 'title',
          Cell: (row) => getLoanTitle(row.index),
        },
        {
          Header: t('loans:table.header_status'),
          id: 'status',
          Cell: (row) => (
            <span data-automation-id={`loan-status-column-${row.index}`}>{getLoanStatus(row.original)}</span>
          ),
        },
        {
          Header: `${t('loans:table.header_total_repaid')} ${currencySymbol}`,
          id: 'totalRepaid',
          Cell: (row) => (
            row.original.summary ? (
              <span>
                <Money cents={row.original.summary.paidAmount} />
                <span className="u-font-color-grey"> {getFormattedRepaidPercent(row.original)}</span>
              </span>
            ) : ''
          ),
        },
        {
          Header: t('loans:table.header_stripe_link'),
          id: 'stripeLink',
          Cell: (row) => (
            <button
              className="c-btn c-btn--blank c-btn--link u-ph0"
              type="button"
              onClick={onLoanLinkClicked(row.original)}
            >
              {t('loans:table.see_details')}
            </button>
          ),
        },
      ]}
      SubComponent={row => (row.original.summary ? (
        <div
          className="u-color-white c-loans-table--expanded-row"
          data-automation-id={`loans-table-expanded-row-${row.index}`}
        >
          <ReactTable
            data={[row.original]}
            showPagination={false}
            minRows={0}
            resizable={false}
            className="c-table-new"
            columns={[
              {
                Header: t('loans:table.header_total_amount_due'),
                id: 'loanAmount',
                Cell: (subRow) => <Money cents={getTotalLoanAmount(subRow.original)} />,
              },
              {
                Header: t('loans:table.header_first_paydown_date'),
                id: 'firstPaydownDate',
                accessor: (loan) => getFirstPaydownDate(loan, timezone),
              },
              {
                Header: t('loans:table.header_repayment_rate'),
                id: 'repaymentRate',
                accessor: getRepaymentRate,
              },
              {
                Header: t('loans:table.header_paid_this_period'),
                id: 'paidThisPeriod',
                Cell: (subRow) => (
                  subRow.original.summary ?
                    <Money cents={subRow.original.summary.currentRepaymentInterval.paidAmount} /> :
                    ''
                ),
              },
            ]}
          />
        </div>
      ) : null)}
      ExpanderComponent={row => (row.original.summary ? (
        <div
          className={classnames('rt-expander', row.isExpanded && '-open')}
          data-automation-id={`row-expander-${row.index}`}
        >&bull;</div>
      ) : null)}
    />
  </div>
);

LoansTable.propTypes = {
  loans: PropTypes.array.isRequired,
  onLoanLinkClicked: PropTypes.func.isRequired,
  currencySymbol: PropTypes.string.isRequired,
  timezone: PropTypes.string.isRequired,
};

export default LoansTable;
